import { useQuery, useMutation } from '@tanstack/react-query';
import { IClinicalRecord } from './types';
import usePatientClinicalHistoryService from './clinicalHistory';

import { StatusCodes } from 'http-status-codes';
import axios from 'axios';
import useMixpanel from '../../hooks/useMixpanel';
import {
  DEFAULT_RETRY_NUMBERS,
  DEFAULT_RETRY_DELAY,
} from '../../config/query-client';

export default function useClinicalRecordService(patientUuid: string) {
  const queryKey = ['patients', patientUuid, 'clinical_record'];
  const { trackFail } = useMixpanel();

  const result = useQuery<IClinicalRecord>({
    queryKey,
    retry: DEFAULT_RETRY_NUMBERS,
    retryDelay: DEFAULT_RETRY_DELAY,
    enabled: !!patientUuid,
  });

  const clinicalHistory = usePatientClinicalHistoryService(
    patientUuid,
    queryKey
  );

  const updateAction = useMutation({
    mutationFn: async (data: Partial<IClinicalRecord>) => {
      await axios.put(`/api/patients/${patientUuid}/clinical_record`, data);
      result.refetch();
    },
  });

  const markOnboardingAction = useMutation({
    mutationFn: async () => {
      try {
        await axios.post(
          `/api/patients/${patientUuid}/clinical_record/mark_onboarding_goal_achieved`
        );
      } catch (error) {
        if (error.response.status === StatusCodes.CONFLICT) {
          throw (
            error.response?.data?.message ?? 'failed to mark as goal achieved'
          );
        } else {
          trackFail('mark_onboarding_goal_achieved', error);
        }
      }
      result.refetch();
    },
  });

  return {
    refetch: () => result.refetch(),
    record: {
      ...(result?.data ?? ({} as IClinicalRecord)),
      objectives: [],
    } as IClinicalRecord,
    setRecord: updateAction.mutateAsync,
    markOnboardingGoalAchieved: markOnboardingAction.mutateAsync,
    ...clinicalHistory,
    state: {
      isRefetching: result?.isRefetching,
      isError: result?.isError ?? false,
      isLoading:
        (!!patientUuid && (result?.isLoading || result?.isRefetching)) ?? false,
    },
  };
}
